// PurchaseReturn_Entry.jsx
// Connects to Node backend:
//   GET  /api/purchaseReturn/entryInit  → supplier dropdown
//   GET  /api/purchaseReturn/entryList  → GRN search (top table)
//   GET  /api/purchaseReturn/items      → items for selected GRN (bottom table)
//   GET  /api/purchaseReturn/view       → fetch existing return by purRetId
//   POST /api/purchaseReturn/insert     → save new purchase return
//
// View mode  : navigate({ state: { purRetId, isViewMode: true } })
// Edit mode  : navigate({ state: { purRetId, isViewMode: false } })
// New mode   : navigate with no state

import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import {
  ArrowLeft,
  Save,
  Search,
  RefreshCw,
  FileText,
  Package,
  Building2,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const BASE_URL = "http://nissi-india.com/api/purchaseReturn";

/* ─── Helpers ───────────────────────────────────────────────────────────── */
const toApiDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

const fromApiDate = (value) => {
  if (!value) return "";

  if (value.includes("T")) {
    const dateOnly = value.split("T")[0];
    const [y, m, d] = dateOnly.split("-");
    if (!y || !m || !d) return "";
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  if (value.includes("-") && value.split("-")[0].length === 4) {
    return value;
  }

  const normalized = value.includes("-") ? value.replace(/-/g, "/") : value;
  const parts = normalized.split("/");
  if (parts.length !== 3) return "";

  const [d, m, y] = parts;
  if (!d || !m || !y) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

const numVal = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const getTodayInputDate = () => new Date().toISOString().split("T")[0];

const getMinusDaysInputDate = (days) => {
  const dt = new Date();
  dt.setDate(dt.getDate() - days);
  return dt.toISOString().split("T")[0];
};

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
const PurchaseReturnEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  /* ── Mode info from listing ─────────────────────────────── */
  const purRetIdFromState = location.state?.purRetId || null;
  const isViewModeInit = location.state?.isViewMode || false;
  const isExistingMode = !!purRetIdFromState;
  const [isViewMode, setIsViewMode] = useState(isViewModeInit);

  /* ── session / localStorage ─────────────────────────────── */
  const storedBranch =
    localStorage.getItem("branch") ||
    localStorage.getItem("Branch") ||
    localStorage.getItem("branchType") ||
    "";

  const storedBranchId = parseInt(
    localStorage.getItem("branchId") ||
      localStorage.getItem("branchid") ||
      "8",
    10
  );

  const storedUserId = parseInt(
    localStorage.getItem("userId") ||
      localStorage.getItem("userid") ||
      "37",
    10
  );

  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("finyearid") ||
    "";

  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("finyear") ||
    "";

  const initialBranchType =
    storedBranch === "HO" || storedBranch === "HEAD-OFFICE" ? "HO" : "Branch";

  const inputClass = (readOnly = false) =>
    `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
      readOnly
        ? isDark
          ? "bg-slate-900/60 border-slate-700 text-slate-400 cursor-not-allowed"
          : "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
        : isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ─── Top Section (GRN Search) state ────────────────────── */
  const [branchType, setBranchType] = useState(initialBranchType);
  const [supplierList, setSupplierList] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [grnNo, setGrnNo] = useState("");
  const [confNo, setConfNo] = useState("");
  const [insNo, setInsNo] = useState("");

  const [grnList, setGrnList] = useState([]);
  const [grnLoading, setGrnLoading] = useState(false);
  const [selectedGrn, setSelectedGrn] = useState(null);

  /* ─── Bottom Section (Return Detail) state ───────────────── */
  const [purRetNo, setPurRetNo] = useState("");
  const [refNo, setRefNo] = useState("");
  const [date, setDate] = useState("");
  const [refDate, setRefDate] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierAddress, setSupplierAddress] = useState("");
  const [parcel, setParcel] = useState("");
  const [lrNo, setLRNo] = useState("");
  const [remarks, setRemarks] = useState("");

  const [itemsData, setItemsData] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ── API headers ─────────────────────────────────────────── */
  const apiHeaders = useMemo(
    () => ({
      ...(finYearId ? { "x-finyearid": finYearId } : {}),
      ...(finYear ? { "x-finyear": finYear } : {}),
      "x-branchid": String(storedBranchId),
      "x-branch": storedBranch,
      "x-userid": String(storedUserId),
      "x-branchtype": branchType,
    }),
    [finYearId, finYear, storedBranchId, storedBranch, storedUserId, branchType]
  );

  const applyDefaultSearchDates = () => {
    setToDate(getTodayInputDate());
    setFromDate(getMinusDaysInputDate(7));
  };

  /* ─────────────────────────────────────────────────────────
     1. ENTRY INIT — load supplier dropdown
  ───────────────────────────────────────────────────────── */
  useEffect(() => {
    const loadInit = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/entryInit`, {
          params: { branchId: storedBranchId, branchType },
          headers: apiHeaders,
        });

        if (!res.data.success) throw new Error(res.data.message);

        setSupplierList(res.data.suppliers || []);

        if (!isExistingMode) {
          if (res.data.defaultFromDate) {
            setFromDate(fromApiDate(res.data.defaultFromDate));
          } else {
            setFromDate(getMinusDaysInputDate(7));
          }

          if (res.data.defaultToDate) {
            setToDate(fromApiDate(res.data.defaultToDate));
          } else {
            setToDate(getTodayInputDate());
          }

          if (res.data.defaultDate && !date) {
            setDate(fromApiDate(res.data.defaultDate));
          }
        }
      } catch (err) {
        console.error("entryInit error:", err);
        toast.error("Failed to load suppliers");
        if (!isExistingMode) {
          applyDefaultSearchDates();
        }
      }
    };

    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchType, isExistingMode]);

  /* ─────────────────────────────────────────────────────────
     2. FETCH EXISTING PURCHASE RETURN FOR BOTH VIEW + EDIT
  ───────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!purRetIdFromState) return;

    const loadExistingPurchaseReturn = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/view`, {
          params: { purRetId: purRetIdFromState },
          headers: apiHeaders,
        });

        if (!res.data.success) {
          throw new Error(res.data.message || "Failed to fetch purchase return");
        }

        const payload = res.data.data || {};
        const header = payload.header || {};
        const items = payload.items || [];

        const dbBranchType =
          header.branchType ||
          header.fld_branchtype ||
          header.BranchType ||
          initialBranchType;

        setBranchType(dbBranchType === "HO" ? "HO" : "Branch");
        setPurRetNo(header.fld_purchaseretno || "");
        setSupplier(String(header.fld_supplierid || ""));
        setDate(fromApiDate(header.fld_date || ""));
        setRefNo(header.fld_refno || header.fld_RefNo || "");
        setRefDate(fromApiDate(header.fld_RefDate || header.fld_refdate || ""));
        setRemarks(header.fld_Remarks || "");
        setParcel(header.fld_CourierService || "");
        setLRNo(header.fld_LRNo || "");
        setSupplierName(header.fld_suppliername || "");
        setSupplierAddress(
          header.fld_Address1 ||
            header.fld_address1 ||
            header.fld_supplieraddress ||
            ""
        );

        const headerDate = fromApiDate(header.fld_date || "");
        setFromDate(headerDate || "");
        setToDate(headerDate || "");

        const firstItem = items[0] || {};
        const grnRow = {
          fld_grnid: firstItem.fld_grnid || header.fld_grnid || "",
          FLD_grnNo:
            header.fld_grnno || header.FLD_grnNo || header.fld_GRNNo || "",
          fld_date:
            header.fld_grndate || header.grnDate || firstItem.fld_grndate || "",
          fld_confdate:
            header.fld_confdate || firstItem.fld_confdate || "",
          fld_suppliername: header.fld_suppliername || "",
          fld_address1:
            header.fld_Address1 || header.fld_address1 || "",
        };

        if (grnRow.fld_grnid) {
          setGrnList([grnRow]);
          setSelectedGrn(grnRow);
        } else {
          setGrnList([]);
          setSelectedGrn(null);
        }

        const mappedItems = items.map((item) => ({
          ...item,
          selected: true,
          ReturnQty:
            item.ReturnQty ??
            item.fld_returnqty ??
            item.returnqty ??
            "",
          RejectedQty:
            item.RejectedQty ??
            item.fld_rejectedqty ??
            item.rejectqty ??
            "",
        }));

        setItemsData(mappedItems);
      } catch (err) {
        console.error("View/Edit fetch error:", err);
        toast.error(
          err.response?.data?.message ||
            err.message ||
            "Failed to load purchase return details"
        );
      }
    };

    loadExistingPurchaseReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purRetIdFromState]);

  /* ─────────────────────────────────────────────────────────
     3. BRANCH TYPE CHANGE
  ───────────────────────────────────────────────────────── */
  const handleBranchTypeChange = (val) => {
    if (isExistingMode) return;

    setBranchType(val);
    setSupplier("");
    setGrnNo("");
    setConfNo("");
    setInsNo("");
    setGrnList([]);
    setItemsData([]);
    setSelectedGrn(null);
    setSupplierName("");
    setSupplierAddress("");
  };

  /* ─────────────────────────────────────────────────────────
     4. GRN SEARCH (top table)
  ───────────────────────────────────────────────────────── */
  const handleSearch = async () => {
    if (!supplier) {
      toast.error("Please select a Supplier");
      return;
    }

    if (!fromDate || !toDate) {
      toast.error("Please select From Date and To Date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    setGrnLoading(true);
    setGrnList([]);
    setItemsData([]);
    setSelectedGrn(null);

    try {
      const res = await axios.get(`${BASE_URL}/entryList`, {
        params: {
          supplierid: supplier,
          branchId: storedBranchId,
          branchType,
          fromDate: toApiDate(fromDate),
          toDate: toApiDate(toDate),
          grnNo: grnNo || "",
          confNo: confNo || "",
          insNo: insNo || "",
          reqType: branchType === "HO" ? "H" : "B",
          purRetId: purRetIdFromState || 0,
        },
        headers: apiHeaders,
      });

      if (!res.data.success) throw new Error(res.data.message);

      const rows = res.data.data || [];
      setGrnList(rows);

      if (rows.length === 0) {
        toast.info("No GRN records found");
      } else if (rows[0]?.fld_suppliername) {
        setSupplierName(rows[0].fld_suppliername);
        setSupplierAddress(rows[0].fld_address1 || "");
      }
    } catch (err) {
      console.error("entryList error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch GRN records");
    } finally {
      setGrnLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     5. GRN CHECKBOX → load items
  ───────────────────────────────────────────────────────── */
const handleGrnCheck = async (row, checked) => {
  console.log("GRN check", { checked, grnId: row?.fld_grnid, row });

  if (isExistingMode || isViewMode) return;

  if (!checked) {
    if (selectedGrn?.fld_grnid === row.fld_grnid) {
      setSelectedGrn(null);
      setItemsData([]);
    }
    return;
  }

  setSelectedGrn(row);
  setItemsLoading(true);

  try {
    const res = await axios.get(`${BASE_URL}/items`, {
      params: {
        grnId: row.fld_grnid,
        flag: "N",
        purRetId: 0,
      },
      headers: apiHeaders,
    });

    console.log("items response", res.data);

    if (!res.data.success) throw new Error(res.data.message);

    setItemsData(
      (res.data.data || []).map((i) => ({
        ...i,
        selected: false,
        ReturnQty: i.ReturnQty ?? i.fld_returnqty ?? "",
        RejectedQty: i.RejectedQty ?? i.fld_rejectedqty ?? "",
      }))
    );
    // ... rest unchanged

      if (res.data.supplierInfo) {
        setSupplierName(res.data.supplierInfo.fld_suppliername || "");
        setSupplierAddress(res.data.supplierInfo.fld_address1 || "");
      }

      if (!date) {
        setDate(getTodayInputDate());
      }
    } catch (err) {
      console.error("items error:", err);
      toast.error("Failed to load items for selected GRN");
    } finally {
      setItemsLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     6. ITEM CHANGES
  ───────────────────────────────────────────────────────── */
  const handleItemQtyChange = (index, field, val) => {
    setItemsData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const handleItemSelectChange = (index, checked) => {
    setItemsData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: checked };
      return updated;
    });
  };

  /* ─────────────────────────────────────────────────────────
     7. SAVE / INSERT
  ───────────────────────────────────────────────────────── */
  const handleInsert = async () => {
    if (!date) {
      toast.error("Please select a Return Date");
      return;
    }

    if (!supplier) {
      toast.error("Please select a Supplier");
      return;
    }

    if (!selectedGrn && !isExistingMode) {
      toast.error("Please select a GRN");
      return;
    }

    if (selectedGrn?.fld_confdate) {
      const confDateStr = fromApiDate(selectedGrn.fld_confdate);
      if (confDateStr) {
        const confDate = new Date(confDateStr);
        const entryDate = new Date(date);
        if (entryDate < confDate) {
          toast.error(
            "Return Date must be greater than or equal to GRN Confirmation Date"
          );
          return;
        }
      }
    }

    const selectedItems = itemsData
      .filter((i) => i.selected)
      .map((i) => ({
        grnid: selectedGrn?.fld_grnid || i.fld_grnid || 0,
        itemid: i.fld_itemid || 0,
        rejectqty: numVal(i.RejectedQty),
        returnqty: numVal(i.ReturnQty),
        poid: i.fld_PurchaseOrderid || i.fld_purchaseorderid || 0,
        projectid: i.fld_projectid || 0,
        storeid: i.fld_storeid || 0,
        price: numVal(i.fld_price),
      }));

    if (selectedItems.length === 0) {
      toast.error("Please select at least one item");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/insert`,
        {
          payload: {
            branchid: storedBranchId,
            userid: storedUserId,
            supplierid: Number(supplier),
            grnDate: toApiDate(date),
            refNo,
            refDate: refDate ? toApiDate(refDate) : "",
            remarks,
            courierService: parcel,
            lrno: lrNo,
            purRetId: purRetIdFromState || 0,
            items: selectedItems,
          },
        },
        { headers: apiHeaders }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Saved successfully");
        setTimeout(() => navigate("/inventory/PurchaseReturn_Listing"), 1800);
      } else {
        toast.error(res.data.message || "Save failed");
      }
    } catch (err) {
      console.error("Insert/Update error:", err);
      const msg =
        err.response?.data?.message ||
        "Failed to save. Please refresh and try again.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ─────────────────────────────────────────────────────────
     8. ADD NEW / RESET
  ───────────────────────────────────────────────────────── */
  const handleAddNew = () => {
    setIsViewMode(false);
    setSupplier("");
    setGrnNo("");
    setConfNo("");
    setInsNo("");
    setGrnList([]);
    setSelectedGrn(null);
    setRefNo("");
    setDate("");
    setRefDate("");
    setParcel("");
    setLRNo("");
    setRemarks("");
    setSupplierName("");
    setSupplierAddress("");
    setItemsData([]);
    setPurRetNo("");
    setBranchType(initialBranchType);
    applyDefaultSearchDates();
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            {isViewMode
              ? "Purchase Return – View"
              : isExistingMode
              ? "Purchase Return – Edit"
              : "Purchase Return Entry"}
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Top Header */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {isViewMode
                  ? "View Purchase Return"
                  : isExistingMode
                  ? "Edit Purchase Return"
                  : "New Purchase Return"}
              </h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {isViewMode
                  ? "Read-only view of existing return"
                  : "Search GRN and create return document"}
              </p>
            </div>
            {purRetNo && (
              <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                PR No: {purRetNo}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isViewMode && !isExistingMode && (
              <button
                onClick={handleAddNew}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <RefreshCw size={16} />
                Reset
              </button>
            )}

            <button
              onClick={() => navigate("/inventory/PurchaseReturn_Listing")}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
        </div>

        {/* SECTION 1 — GRN SEARCH */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <Search size={20} className={`text-${baseColor}-600`} />
              <h3 className="text-xl font-bold">GRN Search</h3>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Branch Type */}
            <div>
              <label className={labelClass}>Branch Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleBranchTypeChange("HO")}
                  disabled={storedBranch !== "HO" || isExistingMode}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    branchType === "HO"
                      ? `${buttonClass} ${hoverClass} text-white shadow`
                      : isDark
                      ? "bg-slate-800 text-slate-300 border border-slate-600"
                      : "bg-gray-100 text-gray-700 border border-gray-200"
                  } ${
                    storedBranch !== "HO" || isExistingMode
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <Building2 size={16} />
                  H.O
                </button>

                <button
                  type="button"
                  onClick={() => handleBranchTypeChange("Branch")}
                  disabled={storedBranch === "HO" || isExistingMode}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    branchType === "Branch"
                      ? "bg-blue-600 text-white shadow"
                      : isDark
                      ? "bg-slate-800 text-slate-300 border border-slate-600"
                      : "bg-gray-100 text-gray-700 border border-gray-200"
                  } ${
                    storedBranch === "HO" || isExistingMode
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  Branch
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label className={labelClass}>
                  Supplier <span className="text-red-500">*</span>
                </label>
                <select
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  disabled={isExistingMode}
                  className={inputClass(isExistingMode)}
                >
                  <option value="">Select</option>
                  {supplierList.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  From Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={isExistingMode}
                  className={inputClass(isExistingMode)}
                />
              </div>

              <div>
                <label className={labelClass}>
                  To Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={isExistingMode}
                  className={inputClass(isExistingMode)}
                />
              </div>

              <div>
                <label className={labelClass}>GRN No</label>
                <input
                  type="text"
                  value={grnNo}
                  onChange={(e) => setGrnNo(e.target.value)}
                  disabled={isExistingMode}
                  placeholder="Search GRN No"
                  className={inputClass(isExistingMode)}
                />
              </div>

              <div>
                <label className={labelClass}>Confirm No</label>
                <input
                  type="text"
                  value={confNo}
                  onChange={(e) => setConfNo(e.target.value)}
                  disabled={isExistingMode}
                  placeholder="Confirm No"
                  className={inputClass(isExistingMode)}
                />
              </div>

              <div>
                <label className={labelClass}>Inspection No</label>
                <input
                  type="text"
                  value={insNo}
                  onChange={(e) => setInsNo(e.target.value)}
                  disabled={isExistingMode}
                  placeholder="Inspection No"
                  className={inputClass(isExistingMode)}
                />
              </div>
            </div>

            {!isExistingMode && (
              <div className="flex justify-end">
                <button
                  onClick={handleSearch}
                  disabled={grnLoading}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  {grnLoading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Search size={16} />
                  )}
                  {grnLoading ? "Searching…" : "Search GRN"}
                </button>
              </div>
            )}

            {/* GRN Results */}
            {grnLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
                <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Loading GRN records…
              </div>
            ) : grnList.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700">
                <table className="w-full text-sm">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      {["S.No", "GRN No", "Date", "Confirm Date", "Select"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${
                      isDark ? "divide-gray-600" : "divide-gray-200"
                    }`}
                  >
                    {grnList.map((row, i) => (
                      <tr
                        key={row.fld_grnid || i}
                        className={`transition ${
                          selectedGrn?.fld_grnid === row.fld_grnid
                            ? isDark
                              ? "bg-blue-900/30"
                              : "bg-blue-50"
                            : isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-center font-medium">
                          {i + 1}
                        </td>
                        <td className="px-4 py-3 font-semibold">
                          {row.FLD_grnNo || row.fld_grnno || "—"}
                        </td>
                        <td className="px-4 py-3">{row.fld_date || "—"}</td>
                        <td className="px-4 py-3">{row.fld_confdate || "—"}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            disabled={isExistingMode || isViewMode}
                            checked={selectedGrn?.fld_grnid === row.fld_grnid}
                            onChange={(e) =>
                              handleGrnCheck(row, e.target.checked)
                            }
                            className="w-4 h-4 rounded cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>

        {/* SECTION 2 — RETURN DETAILS */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <Package size={20} className={`text-${baseColor}-600`} />
              <h3 className="text-xl font-bold">Return Details</h3>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label className={labelClass}>Ref No</label>
                <input
                  type="text"
                  value={refNo}
                  onChange={(e) => setRefNo(e.target.value)}
                  disabled={isViewMode}
                  placeholder="Reference No"
                  className={inputClass(isViewMode)}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={isViewMode}
                  className={inputClass(isViewMode)}
                />
              </div>

              <div>
                <label className={labelClass}>Ref Date</label>
                <input
                  type="date"
                  value={refDate}
                  onChange={(e) => setRefDate(e.target.value)}
                  disabled={isViewMode}
                  className={inputClass(isViewMode)}
                />
              </div>

              <div>
                <label className={labelClass}>Supplier Name</label>
                <input
                  type="text"
                  value={supplierName}
                  readOnly
                  className={inputClass(true)}
                />
              </div>

              <div>
                <label className={labelClass}>Parcel Service / Courier Name</label>
                <input
                  type="text"
                  value={parcel}
                  onChange={(e) => setParcel(e.target.value)}
                  disabled={isViewMode}
                  placeholder="Courier / Parcel service"
                  className={inputClass(isViewMode)}
                />
              </div>

              <div>
                <label className={labelClass}>LR No</label>
                <input
                  type="text"
                  value={lrNo}
                  onChange={(e) => setLRNo(e.target.value)}
                  disabled={isViewMode}
                  placeholder="LR Number"
                  className={inputClass(isViewMode)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className={labelClass}>Supplier Address</label>
                <textarea
                  value={supplierAddress}
                  readOnly
                  rows={2}
                  className={`${inputClass(true)} resize-none`}
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-3">
                <label className={labelClass}>Remarks</label>
                <textarea
                  value={remarks}
                  rows={2}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={isViewMode}
                  placeholder="Any remarks…"
                  className={`${inputClass(isViewMode)} resize-none`}
                />
              </div>
            </div>

            {/* Items Table */}
            {itemsLoading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-sm text-gray-400">
                <span className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Loading items…
              </div>
            ) : itemsData.length > 0 ? (
              <>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700">
                  <table className="w-full text-sm min-w-[1100px]">
                    <thead
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                    >
                      <tr>
                        {[
                          "S.No",
                          "Item Code",
                          "Item Name",
                          "UOM",
                          "Order Qty",
                          "GRN Qty",
                          "Accepted Qty",
                          "Return Qty",
                          "Rejected Qty",
                          "Price",
                          "Select",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody
                      className={`divide-y ${
                        isDark ? "divide-gray-600" : "divide-gray-200"
                      }`}
                    >
                      {itemsData.map((item, index) => (
                        <tr
                          key={item.fld_grndetid || index}
                          className={`transition ${
                            item.selected
                              ? isDark
                                ? "bg-blue-900/30"
                                : "bg-blue-50"
                              : isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-3 py-2.5 text-center font-medium">
                            {index + 1}
                          </td>
                          <td className="px-3 py-2.5 font-semibold whitespace-nowrap">
                            {item.fld_itemcode || "—"}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {item.fld_itemname || "—"}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {item.fld_uomname || "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {item.fld_orderqty ?? "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {item.fld_grnqty ?? "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {item.fld_acceptedqty ?? "—"}
                          </td>

                          <td className="px-3 py-2.5">
                            <input
                              type="number"
                              min="0"
                              value={item.ReturnQty ?? ""}
                              onChange={(e) =>
                                handleItemQtyChange(
                                  index,
                                  "ReturnQty",
                                  e.target.value
                                )
                              }
                              disabled={isViewMode}
                              className={`w-24 px-2.5 py-2 border rounded-lg text-sm text-right ${
                                isViewMode
                                  ? isDark
                                    ? "bg-slate-900/60 border-slate-700 text-slate-400"
                                    : "bg-gray-100 border-gray-200 text-gray-500"
                                  : isDark
                                  ? "bg-slate-800/60 border-slate-600 text-white"
                                  : "bg-white border-slate-300"
                              } focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30`}
                            />
                          </td>

                          <td className="px-3 py-2.5">
                            <input
                              type="number"
                              min="0"
                              value={item.RejectedQty ?? ""}
                              onChange={(e) =>
                                handleItemQtyChange(
                                  index,
                                  "RejectedQty",
                                  e.target.value
                                )
                              }
                              disabled={isViewMode}
                              className={`w-24 px-2.5 py-2 border rounded-lg text-sm text-right ${
                                isViewMode
                                  ? isDark
                                    ? "bg-slate-900/60 border-slate-700 text-slate-400"
                                    : "bg-gray-100 border-gray-200 text-gray-500"
                                  : isDark
                                  ? "bg-slate-800/60 border-slate-600 text-white"
                                  : "bg-white border-slate-300"
                              } focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30`}
                            />
                          </td>

                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {item.fld_price ?? "—"}
                          </td>

                          <td className="px-3 py-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={item.selected || false}
                              disabled={isViewMode}
                              onChange={(e) =>
                                handleItemSelectChange(index, e.target.checked)
                              }
                              className="w-4 h-4 rounded cursor-pointer"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!isViewMode && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleInsert}
                      disabled={saving}
                      className={`inline-flex items-center gap-2 px-8 py-3 text-white text-sm font-bold rounded-xl shadow-lg transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                    >
                      {saving ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save size={18} />
                      )}
                      {saving
                        ? "Saving…"
                        : isExistingMode
                        ? "Update Purchase Return"
                        : "Save Purchase Return"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div
                className={`text-center py-12 text-sm ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {grnList.length > 0
                  ? "Select a GRN from the table above to load items"
                  : "Search for GRN records using the form above"}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseReturnEntry;